import { relations, sql } from 'drizzle-orm';
import { boolean, decimal, index, integer, numeric, pgEnum, pgTable, text, uuid } from 'drizzle-orm/pg-core';
import * as z from 'zod';
import { Status } from '../../util/schemas.js';
import { user } from './auth.js';
import { companies, containerMaterials, containerTypes, stabilityRules, units } from './basic-data.js';
import { drugCatalogItems, medicationProfiles } from './drug-catalog.js';
import { batches } from './stocks.js';
import { timestamps } from './utils/index.js';
import { userActions } from './utils/user-actions.js';
import { uuidv7 } from './utils/uuidv7.js';
/* ------------------------------------------------------------------
 * Trade names
 * ------------------------------------------------------------------ */
export const ContainerContents = z.enum(['EMPTY', 'FILLED', 'PUMP_NOT_FILLED', 'VIAL_FILLED']);
export const containerContentsEnum = pgEnum('container_contents', [
    ContainerContents.enum.EMPTY,
    ContainerContents.enum.FILLED,
    ContainerContents.enum.PUMP_NOT_FILLED,
    ContainerContents.enum.VIAL_FILLED,
]);
// #region CONSUMABLES
export const consumablesStatusEnum = pgEnum('consumables_status', [Status.enum.active, Status.enum.inactive]);
export const CONSUMABLES_CATEGORIES = [
    'syringes',
    'sealingCones',
    'needles',
    'spikes',
    'pumps',
    'cassettes',
    'emptyBags',
];
export const ConsumableCategory = z.enum(CONSUMABLES_CATEGORIES);
export const consumableCategoryEnum = pgEnum('consumables_category', [
    ConsumableCategory.enum.syringes,
    ConsumableCategory.enum.sealingCones,
    ConsumableCategory.enum.needles,
    ConsumableCategory.enum.spikes,
    ConsumableCategory.enum.pumps,
    ConsumableCategory.enum.cassettes,
    ConsumableCategory.enum.emptyBags,
]);
export const consumables = pgTable('consumables', {
    id: uuidv7('id').primaryKey(),
    cyproOldId: integer('cypro_old_id'),
    name: text('name').notNull(),
    description: text('description'),
    status: consumablesStatusEnum('status').default(Status.enum.active),
    barcode: text('barcode').unique(),
    nationalId: text('national_id').unique(),
    category: consumableCategoryEnum('category'),
    unitsPerPackage: integer('units_per_package').default(1),
    minimumStock: integer('minimum_stock'),
    ...timestamps(),
    ...userActions(),
}, t => [
    index('idx_consumables_active')
        .on(t.deletedAt)
        .where(sql `${t.deletedAt} IS NULL`),
    // the consumables list searches the name with ILIKE
    index('idx_consumables_name_trgm').using('gin', sql `${t.name} gin_trgm_ops`),
]);
// #endregion
// #region TRADE NAMES
export const tradeNameStatusEnum = pgEnum('trade_form_status', [Status.enum.active, Status.enum.inactive]);
export const tradeNames = pgTable('trade_names', {
    id: uuidv7('id').primaryKey(),
    cyproOldId: integer('cypro_old_id'),
    name: text('name').notNull(),
    atcCode: text('atc_code'),
    labelName: text('label_name'),
    prescribedNameId: uuid('prescribed_name_id')
        .notNull()
        .references(() => prescribedNames.id, {
        onDelete: 'restrict',
        onUpdate: 'cascade',
    }),
    nationalId: text('national_id'),
    barcodeGtin: text('barcode_gtin'),
    isTrial: boolean('is_trial'),
    externalInformationLink: text('external_information_link'),
    description: text('description'),
    productionNotes: text('production_notes'),
    // for gravimetric production
    density: numeric('density', { mode: 'number' }),
    medicationProfileId: uuid('medication_profile_id')
        .notNull()
        .references(() => medicationProfiles.id, { onDelete: 'restrict', onUpdate: 'cascade' }),
    manufacturerId: uuid('manufacturer_id').references(() => companies.id, { onDelete: 'restrict', onUpdate: 'cascade' }),
    unitsPerPackage: integer('units_per_package').default(1),
    minimumStock: integer('minimum_stock'),
    containerTypeId: uuid('container_type_id').references(() => containerTypes.id, {
        onDelete: 'restrict',
        onUpdate: 'cascade',
    }),
    containerMaterialId: uuid('container_material_id').references(() => containerMaterials.id, {
        onDelete: 'restrict',
        onUpdate: 'cascade',
    }),
    // only for liquids
    volumeValue: numeric('volume_value', { mode: 'number' }), // always mL — no volume unit column
    overfilledBy: numeric('overfilled_by', { mode: 'number' }), // the extra quantity that's provided by the supplier per 1 unit of the trade name
    actualVolume: numeric('actual_volume', { mode: 'number' }), // the sum of volumeValue and overfilledBy
    // =====
    // only for active substances
    reconstitutionRequired: boolean('reconstitution_required'),
    additionalApprovalRequired: boolean('additional_approval_required'),
    containsAlcohol: boolean('contains_alcohol').notNull().default(false),
    reconstitutedSolutionConcentration: numeric('reconstituted_solution_concentration', { mode: 'number' }),
    reconstitutedConcentrationUnitId: uuid('reconstituted_concentration_unit_id').references(() => units.id, {
        onDelete: 'restrict',
        onUpdate: 'cascade',
    }),
    reconstitutedSolutionVolumeExpansion: numeric('reconstituted_solution_volume_expansion', { mode: 'number' }), // mL
    // =====
    // only for solutions
    containerContents: containerContentsEnum('container_contents'),
    maxVolumeCapacity: decimal('max_volume', { mode: 'number' }),
    concentrationValue: numeric('concentration_value', { mode: 'number' }),
    concentrationUnitId: uuid('concentration_unit_id').references(() => units.id, {
        onDelete: 'restrict',
        onUpdate: 'cascade',
    }),
    // =====
    // only for active substances and supportive medication
    /**
     * For LIQUIDS, the strength value is expressed as strength/ml. Eg. 50mg/ml.
     *
     * For SOLIDS, the strength value is absolute. Eg. 50mg. If you want to find the LIQUID concentration for a SOLID, see `util/calculations`.
     */
    strengthValue: numeric('strength_value', { mode: 'number' }),
    strengthUnitId: uuid('strength_unit_id').references(() => units.id, { onDelete: 'restrict', onUpdate: 'cascade' }),
    // =====
    // only for solutions and supportive medication
    additionalsUsed: boolean('additionals_used'),
    additionalsDetails: text('additionals_details'),
    additionalsWeight: numeric('additionals_weight', { mode: 'number' }), // mg
    // =====
    // rest vial threshold override
    minimumSaveableVolume: numeric('minimum_saveable_volume', { mode: 'number' }),
    status: tradeNameStatusEnum('status').default(Status.enum.active),
    ...timestamps(),
    ...userActions(),
}, t => [
    index('idx_trade_names_active')
        .on(t.deletedAt)
        .where(sql `${t.deletedAt} IS NULL`),
    // prescribed-name / substance lookups join through these
    index('idx_trade_names_prescribed_name').on(t.prescribedNameId),
    index('idx_trade_names_medication_profile').on(t.medicationProfileId),
    index('idx_trade_names_status')
        .on(t.status)
        .where(sql `${t.deletedAt} IS NULL`),
    // the trade-name list searches the name with ILIKE
    index('idx_trade_names_name_trgm').using('gin', sql `${t.name} gin_trgm_ops`),
]);
export const tradeNameReconstitutionSolvents = pgTable('trade_name_reconstitution_solvents', {
    id: uuidv7('id').primaryKey(),
    tradeNameId: uuid('trade_name_id')
        .references(() => tradeNames.id, { onDelete: 'cascade' })
        .notNull(),
    solutionDrugId: uuid('solution_drug_id')
        .references(() => drugCatalogItems.id, { onDelete: 'cascade' })
        .notNull(),
    solventVolume: numeric('solvent_volume', { mode: 'number' }), // per-solvent volume; all solvent rows are used and the total volume is their sum
    ...timestamps(),
    ...userActions(),
}, t => [index('idx_trade_name_reconstitution_solvents_trade_name').on(t.tradeNameId)]);
export const tradeNameConsumables = pgTable('trade_name_consumables', {
    id: uuidv7('id').primaryKey(),
    tradeNameId: uuid('trade_name_id')
        .references(() => tradeNames.id, { onDelete: 'cascade' })
        .notNull(),
    consumableId: uuid('consumable_id')
        .references(() => consumables.id, { onDelete: 'cascade' })
        .notNull(),
    units: integer('units').default(1).notNull(),
    ...timestamps(),
    ...userActions(),
}, t => [index('idx_trade_name_consumables_trade_name').on(t.tradeNameId)]);
export const tradeNameRuleStatus = pgEnum('trade_name_stability_rule_status', [
    Status.enum.active,
    Status.enum.inactive,
]);
export const tradeNameStabilityRules = pgTable('trade_name_stability_rules', {
    id: uuidv7('id').primaryKey(),
    tradeNameId: uuid('trade_name_id')
        .notNull()
        .references(() => tradeNames.id, { onDelete: 'restrict', onUpdate: 'cascade' }),
    stabilityRuleId: uuid('stability_rule_id')
        .notNull()
        .references(() => stabilityRules.id, { onDelete: 'restrict', onUpdate: 'cascade' }),
    ...timestamps(),
    ...userActions(),
}, t => [index('idx_trade_name_stability_rules_trade_name').on(t.tradeNameId)]);
// #endregion
// #region Relations
export const tradeNamesRelations = relations(tradeNames, ({ one, many }) => ({
    created: one(user, { fields: [tradeNames.createdBy], references: [user.id] }),
    strengthUnit: one(units, { fields: [tradeNames.strengthUnitId], references: [units.id] }),
    concentrationUnit: one(units, { fields: [tradeNames.concentrationUnitId], references: [units.id] }),
    reconstitutedConcentrationUnit: one(units, {
        fields: [tradeNames.reconstitutedConcentrationUnitId],
        references: [units.id],
    }),
    reconstitutionSolvents: many(tradeNameReconstitutionSolvents),
    consumables: many(tradeNameConsumables),
    containerMaterial: one(containerMaterials, {
        fields: [tradeNames.containerMaterialId],
        references: [containerMaterials.id],
    }),
    containerType: one(containerTypes, {
        fields: [tradeNames.containerTypeId],
        references: [containerTypes.id],
    }),
    medicationProfile: one(medicationProfiles, {
        fields: [tradeNames.medicationProfileId],
        references: [medicationProfiles.id],
    }),
    prescribedName: one(prescribedNames, {
        fields: [tradeNames.prescribedNameId],
        references: [prescribedNames.id],
    }),
    stabilityRules: many(tradeNameStabilityRules),
    batches: many(batches),
    manufacturer: one(companies, {
        fields: [tradeNames.manufacturerId],
        references: [companies.id],
    }),
}));
export const tradeNameReconstitutionSolventsRelations = relations(tradeNameReconstitutionSolvents, ({ one }) => ({
    tradeName: one(tradeNames, {
        fields: [tradeNameReconstitutionSolvents.tradeNameId],
        references: [tradeNames.id],
    }),
    solutionDrug: one(drugCatalogItems, {
        fields: [tradeNameReconstitutionSolvents.solutionDrugId],
        references: [drugCatalogItems.id],
    }),
}));
export const tradeNameConsumablesRelations = relations(tradeNameConsumables, ({ one }) => ({
    tradeName: one(tradeNames, {
        fields: [tradeNameConsumables.tradeNameId],
        references: [tradeNames.id],
    }),
    consumable: one(consumables, {
        fields: [tradeNameConsumables.consumableId],
        references: [consumables.id],
    }),
}));
export const tradeNameStabilityRulesRelations = relations(tradeNameStabilityRules, ({ one }) => ({
    tradeName: one(tradeNames, {
        fields: [tradeNameStabilityRules.tradeNameId],
        references: [tradeNames.id],
    }),
    stabilityRule: one(stabilityRules, {
        fields: [tradeNameStabilityRules.stabilityRuleId],
        references: [stabilityRules.id],
    }),
}));
export const consumablesRelations = relations(consumables, ({ one, many }) => ({
    created: one(user, { fields: [consumables.createdBy], references: [user.id] }),
    batches: many(batches),
}));
// #endregion
export const prescribedNames = pgTable('prescribed_names', {
    id: uuidv7('id').primaryKey(),
    name: text('name').notNull().unique(),
    drugCatalogItemId: uuid('drug_catalog_item_id')
        .notNull()
        .references(() => drugCatalogItems.id, { onDelete: 'restrict', onUpdate: 'cascade' }),
    ...timestamps(),
    ...userActions(),
}, t => [
    index('idx_prescribed_names_active')
        .on(t.deletedAt)
        .where(sql `${t.deletedAt} IS NULL`),
    // the prescribed-name picker filters by substance and searches by ILIKE on the name
    index('idx_prescribed_names_drug_catalog_item').on(t.drugCatalogItemId),
    index('idx_prescribed_names_name_trgm').using('gin', sql `${t.name} gin_trgm_ops`),
]);
export const prescribedNamesRelations = relations(prescribedNames, ({ one, many }) => ({
    drugCatalogItem: one(drugCatalogItems, {
        fields: [prescribedNames.drugCatalogItemId],
        references: [drugCatalogItems.id],
    }),
    created: one(user, {
        fields: [prescribedNames.createdBy],
        references: [user.id],
    }),
    tradeNames: many(tradeNames),
}));
//# sourceMappingURL=products.js.map